// client/src/api/http.ts — the single fetch wrapper · AUTH-004 (04 §9.3.2; 02 §7.7 names the
// file `api/http.ts`, 04 §2.1 `api/configure.ts` — same artifact).
//
// Feature code never touches `fetch`, never builds URLs, never parses JSON by hand (D6). Every
// request leaves through `api()`, which does the four things §9.3.2's interceptors do: the
// bearer header, an `X-Correlation-Id` the API echoes and logs (D7), the 401 → refresh → replay
// dance, and RFC 9457 parsing into `ApiError`. It was the hand-written stand-in for the
// generated `client.gen.ts` while the contract did not exist (D-180). The contract exists now
// and `api/configure.ts` is the `configureApiClient` of §9.3.2 over it, fed the same hooks;
// this wrapper carries `authApi` until the identity operations move onto the generated SDK,
// and nothing else may ride on it for API data (04 §9.4 rule 1).
//
// The wrapper is a TRANSPORT. It knows nothing about the session store — `configureHttp()` is
// handed three hooks by `src/session/source.ts`, and that module decides what a token is, how
// it is refreshed and what happens when it cannot be.

import { apiBaseUrl } from '@/config/app-meta';

import { toProblemDetails, type ProblemDetails } from './problem-details';

/** A non-2xx answer, carrying the problem document the API sent (02 §7.3). */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails;

  constructor(status: number, problem: ProblemDetails) {
    super(problem.title ?? problem.detail ?? `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
  }
}

export interface HttpHooks {
  /** The in-memory access token, read at request time — never copied, never stored here. */
  getAccessToken: () => string | undefined;
  /** Refresh ahead of expiry, so a request never leaves with a token about to be refused. */
  ensureFreshToken: () => Promise<void>;
  /** 04 §9.3.3 — MUST be single-flight; the wrapper calls it once per 401 and trusts it to coalesce. */
  refreshSession: () => Promise<boolean>;
  /** A 401 the refresh could not cure: clear the session and the cache, go to `/login` (§5.11). */
  onUnauthenticated: () => void;
}

let hooks: HttpHooks | undefined;

/** Called once from the session source. `undefined` disconnects — tests use it. */
export function configureHttp(next: HttpHooks | undefined): void {
  hooks = next;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** JSON-serialised; absent for a bodiless call. */
  body?: unknown;
  signal?: AbortSignal;
}

const AUTH_PREFIX = '/api/v1/auth/';

/**
 * The two `/auth/*` routes that take a bearer token and can therefore legitimately answer 401
 * for an expired one. Everything else under `/auth/` answers 401 to mean "authenticate again",
 * and replaying it would loop — the refresh endpoint most of all (04 §9.3.3).
 */
const BEARER_AUTH_ROUTES: ReadonlySet<string> = new Set([
  `${AUTH_PREFIX}mfa/recovery-codes`,
  `${AUTH_PREFIX}mfa/disable`,
]);

/** Whether a 401 on this path is "the token expired" (refresh and replay) rather than "no". */
export function isReplayable(path: string): boolean {
  return !path.startsWith(AUTH_PREFIX) || BEARER_AUTH_ROUTES.has(path);
}

/**
 * One request. Resolves with the parsed JSON body (or `undefined` for 202/204), rejects with
 * `ApiError` for a problem response and with the fetch rejection for a transport failure.
 */
export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const replayable = isReplayable(path);

  if (hooks && replayable) await hooks.ensureFreshToken();

  let response = await send(path, options);

  if (response.status === 401 && hooks && replayable) {
    if (await hooks.refreshSession()) {
      // Replay ONCE with the fresh token. A second 401 is a real one and is returned as such.
      response = await send(path, options);
    } else {
      hooks.onUnauthenticated();
    }
  }

  return parse<T>(response);
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const headers = new Headers({
    Accept: 'application/json',
    // Echoed and logged by the API (D7, 02 §19): the one string a user can read to support.
    'X-Correlation-Id': crypto.randomUUID(),
    // 02 §18 names this as the SPA's marker on state-changing auth calls; sent everywhere so
    // the server may start checking it without a client release.
    'X-Requested-With': 'Proprietari360',
  });

  const token = hooks?.getAccessToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const init: RequestInit = {
    method: options.method ?? 'GET',
    headers,
    // Same-origin in every environment (02 §16): the refresh cookie rides along on its own
    // path, and nothing here needs to know it exists.
    credentials: 'same-origin',
  };

  if (options.body !== undefined) {
    headers.set('Content-Type', 'application/json');
    init.body = JSON.stringify(options.body);
  }
  if (options.signal) init.signal = options.signal;

  return fetch(apiBaseUrl() + path, init);
}

async function parse<T>(response: Response): Promise<T> {
  if (response.ok) {
    const text = await response.text();
    // 202/204 and any other empty success: there is nothing to parse and nothing to return.
    return (text.length === 0 ? undefined : JSON.parse(text)) as T;
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    body = undefined;
  }

  const problem = toProblemDetails(body);
  const headerCorrelation = response.headers.get('X-Correlation-Id');

  throw new ApiError(response.status, {
    ...problem,
    status: response.status,
    // The API writes the id into the body; a 429 from the limiter or a 502 from the proxy may
    // only carry the header. Either way it is the support handle (§5.11).
    ...(problem.correlationId === undefined && headerCorrelation ? { correlationId: headerCorrelation } : {}),
  });
}
